import { useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { analytics } from "../../analytics/analytics";
import { api } from "../../api/client";
import { PlanMetrics, StageRow, WeatherLine } from "../../components/PlanCard";
import { Button, PageHeader, StatePanel } from "../../components/ui";
import { VenueCard } from "../../components/VenueCard";
import type { PlanStage, VenueSummary } from "../../domain/models";
import { t } from "../../i18n";
import { useAppStore } from "../../state/appStore";

export function PlanDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { result, authenticated, updatePlan, setResult } = useAppStore();
  const plan = useMemo(
    () => result?.plans.find((item) => item.id === id),
    [result, id],
  );
  const [replace, setReplace] = useState<PlanStage | null>(null);
  const query = useQuery({
    queryKey: ["replacements", replace?.id],
    queryFn: () => api.replacements(replace!.id),
    enabled: !!replace,
  });
  if (!plan)
    return (
      <StatePanel
        title="Plan unavailable"
        body="This plan may have expired. Create a fresh set of options."
      />
    );
  const visibleStages = plan.stages.filter((stage) => !stage.removed);
  const requestCount = visibleStages.filter(
    (stage) => stage.bookingMode === "request_to_book",
  ).length;
  function remove(stage: PlanStage) {
    void api.updateStage(stage.id, {
      removed: true,
      rowVersion: stage.rowVersion,
    });
    updatePlan({
      ...plan!,
      stages: plan!.stages.map((item) =>
        item.id === stage.id ? { ...item, removed: true } : item,
      ),
    });
  }
  function select(item: VenueSummary) {
    if (!replace) return;
    updatePlan({
      ...plan!,
      stages: plan!.stages.map((stage) =>
        stage.id === replace.id
          ? {
              ...stage,
              branchId: item.id,
              offeringName: item.venueName,
              venueName: item.venueName,
              branchName: item.branchName,
              address: item.address,
              image: item.image,
              bookingMode: item.bookingModes[0] ?? "walk_in",
              reservationRequired: item.hasRequestToBook,
              rowVersion: stage.rowVersion + 1,
            }
          : stage,
      ),
    });
    analytics.track("place_replaced");
    setReplace(null);
  }
  async function refreshWeather() {
    if (!result) return;
    setResult(await api.getPlans(result.requestId));
    analytics.track("weather_refresh_requested");
  }
  return (
    <div className="page">
      <PageHeader
        back={() => navigate(-1)}
        eyebrow={`PLAN ${plan.alternative}`}
        title={plan.reason}
      />
      <PlanMetrics plan={plan} />
      <WeatherLine plan={plan} />
      {plan.weather.requiresRefresh && (
        <button
          className="text-button weather-refresh"
          onClick={() => void refreshWeather()}
        >
          {t.actions.refreshWeather}
        </button>
      )}
      <div className="timeline timeline--detail">
        {visibleStages.map((stage) => (
          <StageRow
            key={stage.id}
            stage={stage}
            actions={
              authenticated && (
                <div className="stage-actions">
                  <button onClick={() => setReplace(stage)}>
                    {t.actions.replace}
                  </button>
                  <button onClick={() => remove(stage)}>
                    {t.actions.remove}
                  </button>
                  <label>
                    Edit time
                    <input
                      aria-label={`Start time for ${stage.offeringName ?? "stage"}`}
                      type="time"
                      defaultValue={new Date(stage.startsAt).toLocaleTimeString(
                        "en-GB",
                        {
                          hour: "2-digit",
                          minute: "2-digit",
                          timeZone: "Asia/Beirut",
                        },
                      )}
                    />
                  </label>
                </div>
              )
            }
          />
        ))}
      </div>
      {plan.stages.some((stage) => stage.isRedacted) && !authenticated ? (
        <div className="unlock-panel">
          <h2>{t.plans.unlockTitle}</h2>
          <p>{t.plans.unlockBody}</p>
          <Button onClick={() => navigate("/auth")}>{t.actions.signIn}</Button>
        </div>
      ) : requestCount > 0 ? (
        <div className="sticky-action">
          <Button onClick={() => navigate(`/reserve/${plan.id}`)}>
            {t.actions.reserve} ({requestCount})
          </Button>
          <small>{t.plans.reserveNote}</small>
        </div>
      ) : (
        <div className="notice">
          <strong>No booking requests needed</strong>
          <p>Every stage can be visited as a walk-in or without a booking.</p>
        </div>
      )}
      {replace && (
        <div
          className="sheet-backdrop"
          role="presentation"
          onClick={() => setReplace(null)}
        >
          <section
            className="bottom-sheet"
            role="dialog"
            aria-modal="true"
            aria-label="Replacement places"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="sheet-handle" />
            <h2>Replace {replace.offeringName}</h2>
            <p>Options match this stage, time, interests and party needs.</p>
            {query.isLoading && <p>Finding matches…</p>}
            {query.data?.map((item) => (
              <VenueCard key={item.id} venue={item} compact onSelect={select} />
            ))}
          </section>
        </div>
      )}
    </div>
  );
}
